import { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const API_URL = 'https://script.google.com/macros/s/AKfycbyg_Sk9P24UR9ISko3TnWe4j3JxkmTaC5CijbgG2ERXakoU1NsM3tcZxJhaQj3N1m3g/exec';

const ContactPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // バリデーション
    if (!formData.email || !formData.message) {
      setError('メールアドレスとお問い合わせ内容は必須です');
      return;
    }

    // メールアドレス形式チェック
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('正しいメールアドレスを入力してください');
      return;
    }

    setIsSubmitting(true);
    setError('');

    // 送信データ（仕様通り）
    const payload = {
      action: 'contact',
      name: formData.name || '未入力',
      email: formData.email,
      message: formData.message,
    };

    console.log('📤 送信データ:', payload);
    console.log('📤 送信先URL:', API_URL);

    try {
      const response = await fetch(API_URL, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'text/plain',
        },
        body: JSON.stringify(payload),
      });

      console.log('📡 レスポンスステータス:', response.status);
      console.log('📡 レスポンスヘッダー:', {
        contentType: response.headers.get('content-type'),
        status: response.status,
        statusText: response.statusText,
      });

      // レスポンステキストを取得
      const responseText = await response.text();
      console.log('📥 レスポンステキスト（生データ）:', responseText);

      // JSONパース
      let result;
      try {
        result = JSON.parse(responseText);
        console.log('📥 パース済みデータ:', result);
      } catch (parseError) {
        console.error('❌ JSONパースエラー:', parseError);
        console.error('❌ パースエラー詳細:', {
          name: parseError instanceof Error ? parseError.name : 'Unknown',
          message: parseError instanceof Error ? parseError.message : String(parseError),
          stack: parseError instanceof Error ? parseError.stack : 'No stack trace',
        });
        console.error('❌ パースできなかった生データ:', responseText);
        setError('サーバーからの応答が不正です。しばらくしてから再度お試しください。');
        setIsSubmitting(false);
        return;
      }

      // ✅ GAS仕様に合わせた判定：errorフィールドの有無で判定
      if (result.error) {
        console.error('❌ GASエラー:', result.error);
        console.error('❌ エラーメッセージ:', result.message || '');
        setError(result.message || result.error || '送信に失敗しました。しばらくしてから再度お試しください。');
        setIsSubmitting(false);
        return;
      }

      // ✅ 成功時：statusの存在確認
      if (result.status !== 'ok') {
        console.error('❌ 不完全なレスポンス:', result);
        setError('サーバーからの応答が不完全です。しばらくしてから再度お試しください。');
        setIsSubmitting(false);
        return;
      }

      console.log('✅ 送信成功！');

      setIsSubmitted(true);

      // 3秒後にフォームをリセット
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({ name: '', email: '', message: '' });
      }, 3000);
    } catch (err) {
      console.error('❌ 送信エラー（catchブロック）:', err);
      console.error('❌ エラーの型:', typeof err);
      console.error('❌ エラーの詳細:', {
        name: err instanceof Error ? err.name : 'Unknown',
        message: err instanceof Error ? err.message : String(err),
        stack: err instanceof Error ? err.stack : 'No stack trace',
        raw: err,
      });
      
      // エラーの詳細情報を表示
      if (err instanceof TypeError && err.message.includes('fetch')) {
        console.error('❌ ネットワークエラー: fetchが失敗しました');
        setError('ネットワークエラー: APIに接続できませんでした。インターネット接続を確認してください。');
      } else if (err instanceof TypeError) {
        console.error('❌ TypeError:', err.message);
        setError(`通信エラー: ${err.message}`);
      } else if (err instanceof Error) {
        console.error('❌ エラーメッセージ:', err.message);
        setError(`送信エラー: ${err.message}`);
      } else {
        console.error('❌ 不明なエラー:', err);
        setError('送信に失敗しました。しばらくしてから再度お試しください。');
      }
      
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-3 cursor-pointer"
          >
            <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-blue-500 to-green-500 rounded-lg">
              <i className="ri-heart-pulse-line text-white text-xl"></i>
            </div>
            <span className="text-xl font-bold text-gray-900">Liver Health Check</span>
          </button>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 pt-32 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">お問い合わせ</h1>
            <p className="text-lg text-gray-600 leading-relaxed">
              ご質問やご意見がございましたら、お気軽にお問い合わせください
            </p>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-lg">
            {!isSubmitted ? (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-bold text-gray-800 mb-3">
                    お名前（任意）
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="山田 太郎"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none transition-colors text-base"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-800 mb-3">
                    メールアドレス <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="example@email.com"
                    required
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none transition-colors text-base"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-800 mb-3">
                    お問い合わせ内容 <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={(e) => {
                      if (e.target.value.length <= 500) {
                        setFormData({ ...formData, message: e.target.value });
                      }
                    }}
                    placeholder="お問い合わせ内容をご記入ください"
                    required
                    rows={6}
                    maxLength={500}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none transition-colors text-base resize-none"
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-2 text-right">
                    {formData.message.length} / 500文字
                  </p>
                </div>

                {error && (
                  <div className="p-4 bg-red-50 border-l-4 border-red-500 rounded-lg">
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                )}

                <div className="p-5 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-gray-700 leading-relaxed">
                    内容を確認のうえ、ご連絡させていただきます。
                    お返事までに数日かかる場合がございますので、予めご了承ください。
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting || !formData.email || !formData.message}
                  className={`w-full px-8 py-4 bg-gradient-to-r from-blue-600 to-green-600 text-white font-bold rounded-lg transition-all duration-300 shadow-lg whitespace-nowrap cursor-pointer ${
                    isSubmitting || !formData.email || !formData.message
                      ? 'opacity-50 cursor-not-allowed'
                      : 'hover:from-blue-700 hover:to-green-700'
                  }`}
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin"></i>
                      送信中...
                    </span>
                  ) : (
                    '送信する'
                  )}
                </button>
              </form>
            ) : (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4 }}
                className="text-center py-12"
              >
                <div className="w-20 h-20 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-6">
                  <i className="ri-check-line text-green-600 text-4xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">送信完了</h3>
                <p className="text-base text-gray-600 leading-relaxed">
                  お問い合わせありがとうございます。<br />
                  内容を確認のうえ、ご連絡させていただきます。
                </p>
              </motion.div>
            )}
          </div>

          <div className="mt-8 text-center">
            <button
              onClick={() => navigate('/')}
              className="text-sm text-gray-600 hover:text-blue-600 transition-colors cursor-pointer"
            >
              ← トップページへ戻る
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ContactPage;
